import { Injectable } from '@angular/core';
import { combineLatest, fromEventPattern, map, pluck, startWith } from 'rxjs';

export type Breakpoint = 'zero' | 'sm' | 'md' | 'prime' | 'lg' | 'xl' | 'aboveXl';

@Injectable({ providedIn: 'root' })
export class ResponsiveUtilsService {
  readonly breakpoints: Record<Breakpoint, number> = {
    zero: 0,
    sm: 576,
    md: 768,
    prime: 960,
    lg: 992,
    xl: 1200,
    aboveXl: 1201,
  };

  private getMatchingBreakpoint() {
    // Create a media query string for each breakpoint
    const bpNames = Object.keys(this.breakpoints) as Breakpoint[];
    const bpValues = Object.values(this.breakpoints);
    const queries = bpValues.map((bp) => `(min-width: ${bp}px)`);

    // Create an observable for each media query
    const matches$ = queries.map((query) => {
      const matchMedia = window.matchMedia(query);

      // Using fromEventPattern() instead from fromEvent() to support Safari prior to v14
      // return fromEvent<MediaQueryListEvent>(matchMedia, 'change').pipe(
      //   map((event) => event.matches),
      //   startWith(matchMedia.matches)
      // );

      return fromEventPattern<MediaQueryListEvent>(
        matchMedia.addListener.bind(matchMedia),
        matchMedia.removeListener.bind(matchMedia)
      ).pipe(
        map((event) => event.matches),
        startWith(matchMedia.matches)
      );
    });

    // Get the last matching breakpoint
    // and return the breakpoint name
    return combineLatest(matches$).pipe(
      map((matches) => {
        return bpNames[matches.lastIndexOf(true)];
      })
    );
  }

  private getResponsiveValue<T>(values: Record<Breakpoint, T>) {
    return this.getMatchingBreakpoint().pipe(map((breakpoint) => values[breakpoint]));
  }

  isBelowXl() {
    return this.getResponsiveValue<boolean>({
      zero: true,
      sm: true,
      md: true,
      prime: true,
      lg: true,
      xl: false,
      aboveXl: false,
    });
  }

  isXlAndAbove() {
    return this.getResponsiveValue<boolean>({
      zero: false,
      sm: false,
      md: false,
      prime: false,
      lg: false,
      xl: true,
      aboveXl: true,
    });
  }

  isAboveXl() {
    return this.getResponsiveValue<boolean>({
      zero: false,
      sm: false,
      md: false,
      prime: false,
      lg: false,
      xl: false,
      aboveXl: true,
    });
  }

  isLgAndAbove() {
    return this.getResponsiveValue<boolean>({
      zero: false,
      sm: false,
      md: false,
      prime: false,
      lg: true,
      xl: true,
      aboveXl: true,
    });
  }

  isBelowPrime() {
    return this.getResponsiveValue<boolean>({
      zero: true,
      sm: true,
      md: true,
      prime: false,
      lg: false,
      xl: false,
      aboveXl: false,
    });
  }

  isTouchDevice() {
    const matchMedia = window.matchMedia('(hover: none) and (pointer: coarse)');
    // return fromEvent<MediaQueryListEvent>(matchMedia, 'change').pipe(pluck('matches'), startWith(matchMedia.matches));
    return fromEventPattern<MediaQueryListEvent>(
      matchMedia.addListener.bind(matchMedia),
      matchMedia.removeListener.bind(matchMedia)
    ).pipe(pluck('matches'), startWith(matchMedia.matches));
  }
}
